import { createAuthTokenFromBackup, signInWithBackup } from "@/lib/auth-flows";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import { INCOMPLETE_INVENTORY_KEY } from "./backup-inventory";
import { commitPortableBackup } from "./durable-backup";
import { encryptIdentityBackup } from "./identity-backup";
import {
	createSeedBackup,
	deriveSeedProfile,
	type SeedBackup,
	validateSeedBackup,
} from "./seed-contract";

type StorageAccess = Pick<Storage, "getItem" | "setItem" | "removeItem">;
export interface SeedRestoreSuccess {
	bapId: string;
	redirectURI?: string;
}
export type SeedRestoreResult =
	| ({ ok: true } & SeedRestoreSuccess)
	| {
			ok: false;
			reason:
				| "invalid"
				| "authentication"
				| "cancelled"
				| "superseded"
				| "storage";
			error: string;
	  };
export interface SeedRestoreDeps {
	local?: StorageAccess;
	session?: StorageAccess;
	signIn?: typeof signInWithBackup;
}
const LOCAL_KEYS = [
	INCOMPLETE_INVENTORY_KEY,
	STORAGE_KEYS.ENCRYPTED_BACKUP,
	STORAGE_KEYS.CLOUD_BACKUP_STALE,
	STORAGE_KEYS.NEW_WALLET_CLOUD_BACKUP_PENDING,
	STORAGE_KEYS.CURRENT_IDENTITY,
	STORAGE_KEYS.PRF_ENCRYPTED_BACKUP,
	STORAGE_KEYS.PRF_VAULT_CATALOG,
	STORAGE_KEYS.PRF_VAULT_PENDING,
	STORAGE_KEYS.PRF_CREDENTIAL_ID,
	STORAGE_KEYS.PRF_SALT,
	STORAGE_KEYS.PRF_ENCRYPTED_ACTIVE_ACCOUNT,
] as const;
const SESSION_KEYS = [
	STORAGE_KEYS.DECRYPTED_BACKUP,
	STORAGE_KEYS.ACTIVE_ACCOUNT,
	STORAGE_KEYS.TEMP_PASSWORD,
	STORAGE_KEYS.REISSUED_BACKUP,
	STORAGE_KEYS.INITIAL_PROFILE_DATA,
] as const;
type Snapshot = Array<{
	storage: StorageAccess;
	key: string;
	value: string | null;
}>;

/** Words recover one explicitly chosen profile, never an inferred inventory. */
export function prepareSeedProfileRestore(
	words: string,
	index: number,
	createdAt = Date.now()
): SeedBackup {
	const base = createSeedBackup(words, createdAt);
	const profile = deriveSeedProfile(base.mnemonic, index);
	return validateSeedBackup({
		...base,
		profiles: [{ index, bapId: profile.bapId }],
		nextProfileIndex: index + 1,
		inventoryComplete: false,
	});
}
function snapshot(local: StorageAccess, session: StorageAccess): Snapshot {
	return [
		...LOCAL_KEYS.map((key) => ({
			storage: local,
			key,
			value: local.getItem(key),
		})),
		...SESSION_KEYS.map((key) => ({
			storage: session,
			key,
			value: session.getItem(key),
		})),
	];
}
function rollback(previous: Snapshot): boolean {
	let restored = true;
	for (const item of previous) {
		try {
			if (item.value === null) {
				item.storage.removeItem(item.key);
			} else {
				item.storage.setItem(item.key, item.value);
			}
		} catch {
			restored = false;
		}
	}
	return restored;
}
function commit(
	backup: SeedBackup,
	encrypted: string,
	previous: Snapshot,
	local: StorageAccess,
	session: StorageAccess
): SeedRestoreResult {
	const selected = backup.profiles[0];
	const account = deriveSeedProfile(backup.mnemonic, selected.index);
	const persisted = commitPortableBackup(
		JSON.stringify(backup),
		encrypted,
		local,
		session
	);
	if (!persisted.success) {
		return {
			ok: false,
			reason: "storage",
			error: persisted.rollbackFailed
				? "Device storage could not be restored completely. Keep your backup file and recovery phrase safe."
				: "Could not save the restored backup. Check device storage before retrying.",
		};
	}
	try {
		session.setItem(
			STORAGE_KEYS.ACTIVE_ACCOUNT,
			JSON.stringify({ wif: account.accountWif, id: selected.bapId })
		);
		local.setItem(STORAGE_KEYS.CURRENT_IDENTITY, selected.bapId);
		// Imported words do not opt into cloud writes or retain another backup's biometric key.
		for (const key of LOCAL_KEYS) {
			if (
				key !== STORAGE_KEYS.ENCRYPTED_BACKUP &&
				key !== STORAGE_KEYS.CURRENT_IDENTITY &&
				key !== INCOMPLETE_INVENTORY_KEY
			) {
				local.removeItem(key);
			}
		}
		for (const key of SESSION_KEYS) {
			if (
				key !== STORAGE_KEYS.DECRYPTED_BACKUP &&
				key !== STORAGE_KEYS.ACTIVE_ACCOUNT
			) {
				session.removeItem(key);
			}
		}
		return { ok: true, bapId: selected.bapId };
	} catch {
		const restored = rollback(previous);
		return {
			ok: false,
			reason: "storage",
			error: restored
				? "Could not save the restored profile. Your previous backup was retained."
				: "Device storage could not be restored completely. Keep your backup file and recovery phrase safe.",
		};
	}
}
function interruption(
	signal: AbortSignal | undefined,
	previous: Snapshot
): SeedRestoreResult | null {
	if (signal?.aborted) {
		return { ok: false, reason: "cancelled", error: "Restore cancelled." };
	}
	if (previous.some((item) => item.storage.getItem(item.key) !== item.value)) {
		return {
			ok: false,
			reason: "superseded",
			error:
				"The backup on this device changed. Review it before retrying restore.",
		};
	}
	return null;
}

/** Encrypt first, prove the existing profile in memory, then commit the exact candidate. */
export async function restoreSeedProfile(
	input: { backup: SeedBackup; password: string; signal?: AbortSignal },
	deps: SeedRestoreDeps = {}
): Promise<SeedRestoreResult> {
	let backup: SeedBackup;
	try {
		backup = structuredClone(validateSeedBackup(input.backup));
		if (backup.profiles.length !== 1 || input.password.length < 8) {
			throw new Error("Invalid restore input");
		}
	} catch {
		return {
			ok: false,
			reason: "invalid",
			error: "Check the recovery phrase, profile number, and backup password.",
		};
	}
	if (input.signal?.aborted) {
		return { ok: false, reason: "cancelled", error: "Restore cancelled." };
	}
	try {
		const local = deps.local ?? localStorage;
		const session = deps.session ?? sessionStorage;
		const previous = snapshot(local, session);
		const encrypted = await encryptIdentityBackup(backup, input.password);
		const beforeProof = interruption(input.signal, previous);
		if (beforeProof) {
			return beforeProof;
		}
		let auth: Awaited<ReturnType<typeof signInWithBackup>>;
		try {
			auth = await (deps.signIn ?? signInWithBackup)(backup, {
				bapId: backup.profiles[0].bapId,
				restoreExistingProfile: true,
				persistLocalState: false,
				navigateOnRedirect: false,
			});
		} catch {
			return {
				ok: false,
				reason: "authentication",
				error:
					"Could not verify an existing profile. Check the phrase and profile number, then retry.",
			};
		}
		const afterProof = interruption(input.signal, previous);
		if (afterProof) {
			return afterProof;
		}
		if (!auth.ok) {
			return {
				ok: false,
				reason: "authentication",
				error:
					"Could not verify an existing profile. Check the phrase and profile number, then retry.",
			};
		}
		const result = commit(backup, encrypted, previous, local, session);
		return result.ok
			? {
					...result,
					...(auth.redirectURI ? { redirectURI: auth.redirectURI } : {}),
				}
			: result;
	} catch {
		return {
			ok: false,
			reason: "storage",
			error:
				"Could not safely restore this backup. Your recovery words have not been sent to the server.",
		};
	}
}

export type SeedProfileRestoreCheck =
	| { ok: true; bapId: string }
	| {
			ok: false;
			reason:
				| "invalid"
				| "not-found"
				| "authentication"
				| "network"
				| "cancelled";
			error: string;
	  };

/** Prove one existing profile in memory before asking for a backup password. */
export async function checkSeedProfileRestore(
	input: SeedBackup,
	signal?: AbortSignal
): Promise<SeedProfileRestoreCheck> {
	const cancelled = (): SeedProfileRestoreCheck => ({
		ok: false,
		reason: "cancelled",
		error: "Profile check cancelled.",
	});
	if (signal?.aborted) {
		return cancelled();
	}
	let account: ReturnType<typeof deriveSeedProfile>;
	try {
		const backup = validateSeedBackup(input);
		if (backup.profiles.length !== 1) {
			throw new Error("Select one profile");
		}
		account = deriveSeedProfile(backup.mnemonic, backup.profiles[0].index);
	} catch {
		return {
			ok: false,
			reason: "invalid",
			error: "Check your recovery words and profile number.",
		};
	}
	const path = "/api/restore/profile-check";
	const body = JSON.stringify({ bapId: account.bapId });
	try {
		const token = createAuthTokenFromBackup(
			{ wif: account.accountWif, id: account.bapId },
			path,
			body
		);
		const response = await fetch(path, {
			method: "POST",
			credentials: "omit",
			cache: "no-store",
			signal,
			headers: { "Content-Type": "application/json", "x-auth-token": token },
			body,
		});
		const result = await response.json();
		if (signal?.aborted) {
			return cancelled();
		}
		if (response.ok && result?.ok === true && result.bapId === account.bapId) {
			return { ok: true, bapId: account.bapId };
		}
		if (
			response.status === 404 &&
			result?.error === "RESTORE_PROFILE_NOT_FOUND"
		) {
			return {
				ok: false,
				reason: "not-found",
				error:
					"No Sigma account found. Check your phrase or use your backup file.",
			};
		}
		if (response.status === 403) {
			return {
				ok: false,
				reason: "authentication",
				error:
					"These recovery words could not prove ownership of this profile.",
			};
		}
	} catch {
		if (signal?.aborted) {
			return cancelled();
		}
	}
	return {
		ok: false,
		reason: "network",
		error: "Couldn’t check your account. Please try again.",
	};
}
